import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import "summernote/dist/summernote-bs5.js";
import "summernote/dist/summernote-bs5.css";
import { mapState } from "pinia";
import turndownService from "turndown";

import { useApp } from "../../../../pages-vue/App/useApp";
import { MessageService } from "../../../../services/message-service";
import { NavigationService } from "../../../../services/navigation.service";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { IGetUserByName } from "../../../vue-interfaces/i-user";
import {
    IConversationNavOptions,
    IConversationUser,
    IMessageItem,
    IMessagePlaceholder
} from "../../../vue-interfaces/messaging-service/i-message";
import { markdownToHtml } from "../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../common/vue-helpers/modifiers/value-modifier";
import {
    formatMessageTime,
    formatTaggedUserMessage,
    sortPlaceholdersList
} from "../../../../common/vue-helpers/modifiers/format-message";
import { truncateString } from "../../../../common/vue-helpers/modifiers/format-message";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";
import { useUserStore } from "../../../../pages-vue/MessageCenter/store/useUsersStore";
interface IFileOption {
    fileName: string;
    iconClass: string;
}
export default defineComponent({
    emits: ["sendMessage", "update:result", "update:selectedProfile", "update:msgFiles"],

    props: {
        options: {
            type: Object,
            default: null
        },
        initialValue: {
            default: "",
            type: String
        },
        result: {
            default: "",
            type: String
        },
        selectedProfile: {
            type: Array,
            default: null
        },
        allSelectedProfiles: {
            type: Array,
            default: null
        },
        isImportant: { type: Boolean, default: false },
        selectedConversation: { type: Object as PropType<IConversationNavOptions>, default: null },
        shouldDisableSend: { type: Boolean, default: false },
        refsName: { type: String, default: "chatEditorRef" },
        editMsg: { type: Object, default: null },
        isFamilyPortal: { type: Boolean, default: false }
    },
    data() {
        return {
            _messageService: null as MessageService,
            _navigationService: null as NavigationService,
            _permissionManager: null as PermissionManager,
            _turndownService: new turndownService(),
            _analyticsService: null,
            editorMarkupRef: null as HTMLElement,
            replyMessageRef: null as HTMLElement,
            editorToolbarRef: null as HTMLElement,
            editor: null as JQuery<HTMLElement>,
            editorLoaded: false as boolean,
            filteredUsers: [] as IGetUserByName[],
            onLoadData: "" as string,
            names: [],
            uploadFile: null as FileList,
            filesUI: null as any,
            expandFilePreview: false as boolean,
            expandProfilesPreview: false as boolean,
            files: [] as File[],
            showAttachmentLimitError: false as boolean,
            showDuplicateAttachmentError: false as boolean,
            replyMessage: null as IMessageItem,
            replyMsgProfiles: [] as IMessagePlaceholder[],
            events: {} as any,
            shouldEnterSend: true as boolean,
            showEmptyMsgError: false as boolean,
            sanitizeMarkup,
            markdownToHtml,
            formatMessageTime,
            truncateString
        };
    },
    created() {
        this._messageService = Container.instance.get(MessageService);
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    async mounted() {
        const editorRef = `${this.refsName}-textMarkup`;
        const replyRef = `${this.refsName}-replyMsg`;
        const toolbarRef = `${this.refsName}-toolbar`;
        this.editorMarkupRef = this.$refs?.[editorRef] as HTMLElement;
        this.replyMessageRef = this.$refs?.[replyRef] as HTMLElement;
        this.editorToolbarRef = this.$refs?.[toolbarRef] as HTMLElement;
        this.create();
        if (this.editMsg) {
            this.files = this.editMsg.files;
            ($(this.editorMarkupRef) as any).summernote("code", this.editMsg.result);
            this.expandProfilesPreview = this.allSelectedProfiles.length > 0;
        }
        if (!!this.onLoadData && this.onLoadData.length > 0) {
            (this.editor as any).summernote("code", this.onLoadData);
        }
        this.editorLoaded = true;
    },

    watch: {
        initialValue: function (newValue) {
            this.initialValueChanged(newValue);
        },

        selectedProfile: function () {
            this.expandProfilesPreview = this.allSelectedProfiles?.length > 0;
        },

        replyMessage: function (newValue, oldValue) {
            this.replyMessageChanged(newValue);
        },

        selectedConversation: {
            handler(newValue, oldValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                    this.reset();
                }
            },
            deep: true
        },
        "selectedConversation.hasUserLeftChat"(newValue) {
            let placeholder = "Start typing here...";
            if (newValue) {
                // reset editor state if user is removed or leaves the group
                ($(this.editorMarkupRef) as any).summernote("code", "");
                if (this.expandFilePreview) {
                    this.files = [];
                    this.expandFilePreview = false;
                }
                if (this.expandProfilesPreview) {
                    this.$emit("update:selectedProfile", [], []);
                    this.expandProfilesPreview = false;
                }
                placeholder = "You can't send messages because you are not a member of the chat.";
                ($(this.editorMarkupRef) as any).summernote("disable");
            } else {
                ($(this.editorMarkupRef) as any).summernote("enable");
            }

            const $placeholder = $(this.editorMarkupRef).next(".note-editor").find(".note-placeholder");
            if ($placeholder.length) {
                $placeholder.text(placeholder);
            }
        },

        files: {
            handler() {
                let filesUI: IFileOption[] = [];
                this.expandFilePreview = this.files?.length > 0;

                if (this.files?.length) {
                    for (const file of this.files) {
                        let fileOption: IFileOption = {
                            fileName: "",
                            iconClass: "fa-file"
                        };
                        fileOption.iconClass = this.getFileIcon(file?.type);
                        fileOption.fileName = file.name;
                        filesUI.push(fileOption);
                    }
                }
                this.filesUI = filesUI;
            },
            deep: true
        }
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useUserStore, ["getMultipleUsers"]),

        defaultOptions(): any {
            let styleOptions = ["bold", "italic", "style"] as const;
            let paraOptions = ["ul", "ol"] as const;
            let insertOptions = ["link"] as const;
            // TODO: Using Summernote.toolbarDef keeps throwing an error
            // even though the definition was followed
            let toolbarOptions: any = [
                ["style", styleOptions],
                ["para", paraOptions],
                ["insert", insertOptions],
                ["custom", ["attach", "important", "shareProfile"]],
                ["editMsgBtns", ["discardBtn", "sendBtn"]]
            ];
            let hintOptions: any = [
                {
                    mentions: this.filteredUsers,
                    match: /\B@(\w*)$/,
                    search: (keyword: any, callback: any) => {
                        this.getChatParticipantsList(keyword)
                            .then((users) => {
                                this.filteredUsers = users as any;
                                this.shouldEnterSend = false;
                                callback(users);
                            })
                            .catch((e) => {
                                console.log(e);
                            });
                    },
                    template: (item: IConversationUser) => {
                        if ((item as any) == "No Matches Found") {
                            return `<div class="text-center name-text loading-text">No Matches Found</div>`;
                        }
                        let nameInitials = "";
                        if (item.firstName) nameInitials += item.firstName.trim().charAt(0);
                        if (item.lastName) nameInitials += item.lastName.trim().charAt(0);
                        return `
                        <div class="m-1">
                            <div class="d-flex">
                                <div class="ava-initials mr-1">
                                    <span>${nameInitials}</span>
                                </div>
                                <div>
                                <div class="d-flex justify-content-between">
                                    <h6 class="name-text">
                                        ${item.firstName} ${item.lastName}
                                    <h6>
                                </div>
                                </div>
                            </div>
                        <div>`;
                    },
                    content: (item: IConversationUser) => {
                        // added extra <span> as the editor keeps running in the same span element when the user continues typing.
                        return $(`<span>`).html(
                            `<span id='${item.loginId}' data-actiontype='tagged-user' contenteditable='false' class="blue-text">${item.firstName} ${item.lastName}</span><span>&nbsp;</span>`
                        )[0];
                    }
                }
            ];
            return {
                toolbar: toolbarOptions,
                styleTags: ["h1", "h2", "h3", "h4", "h5", "h6"],
                placeholder: this.selectedConversation?.hasUserLeftChat
                    ? "You can't send messages because you are not a member of the chat."
                    : "Start typing here...",
                height: 50,
                maxHeight: 50,
                hint: hintOptions,
                toolbarContainer: `.${this.refsName}-editor-toolbar`,
                dialogsInBody: true,
                disableDragAndDrop: true,
                callbacks: {
                    onImageUpload: (files: any) => {
                        // This callback sends Files array instead of FileList,
                        // hence converting it to FileList for upload files logic
                        const dataTransfer = new DataTransfer();
                        files.forEach((file: any) => {
                            dataTransfer.items.add(file);
                        });
                        const fileList = dataTransfer.files;
                        this.uploadFilesChanged(fileList, true);
                    }
                }
            };
        },

        disableSend() {
            const message = this.result ? this.getMarkdownFromHtml(this.result) : "";
            if (this.shouldDisableSend) {
                return true;
            } else return !(message !== "" || this.files?.length > 0 || this.allSelectedProfiles?.length > 0);
        }
    },

    methods: {
        create() {
            let $markupRef = $(this.editorMarkupRef);
            let buttons;
            if (this.editMsg) {
                buttons = {
                    buttons: { attach: this.attachFileButton, sendBtn: this.sendEditBtn, ...this.options?.buttons }
                };
            } else {
                if (!this.isFamilyPortal)
                    buttons = {
                        buttons: { attach: this.attachFileButton, ...this.options?.buttons }
                    };
                else buttons = this.options?.buttons;
            }
            let editorOptions = Object.assign({}, buttons, this.defaultOptions);

            editorOptions.callbacks = {
                ...(editorOptions.callbacks || {}),

                onInit: () => {
                    const range = ($markupRef as any).summernote("editor.createRange");

                    if (range) {
                        range.collapse(true).select();

                        ($markupRef as any).summernote("buttons.updateCurrentStyle");
                    }
                }
            };

            this.editor = ($markupRef as any).summernote(editorOptions);

            if (this.selectedConversation?.hasUserLeftChat) {
                ($(this.editorMarkupRef) as any).summernote("disable");
            }
            this.editor.on("summernote.change", () => {
                this.handleChange();
            });
            this.editor.on("summernote.keydown", (we, e) => {
                const currentNode = window.getSelection().anchorNode.parentNode;
                let nodeName = currentNode.nodeName;
                if (nodeName === "A") {
                    // code to handle case where user adds links inside list
                    nodeName =
                        $(window.getSelection().anchorNode.parentNode).parent()[0].nodeName === "LI" ? "LI" : "A";
                }

                if (["LI", "OL", "UL"].includes(nodeName)) {
                    this.shouldEnterSend = true;
                } else {
                    if (e.key === "Enter" && this.disableSend) {
                        e.preventDefault();
                    } else if (e.key === "Enter" && this.shouldEnterSend && !e.shiftKey && !this.disableSend) {
                        this.send();
                        e.preventDefault();
                    }
                    this.shouldEnterSend =
                        !this.shouldEnterSend && ["ArrowUp", "ArrowDown", "ArrowRight"].includes(e.key) ? false : true;
                }
            });
        },

        handleChange() {
            let content = ($(this.editorMarkupRef) as any).summernote("code") as unknown;
            if (this.result !== content) {
                this.$emit("update:result", content);
            }
        },

        send() {
            if (this.editMsg) {
                const message = this.result ? this.getMarkdownFromHtml(this.result) : "";
                this.showEmptyMsgError = !(
                    message !== "" ||
                    this.files.length > 0 ||
                    this.allSelectedProfiles.length > 0
                );
                if (this.showEmptyMsgError) {
                    setTimeout(() => {
                        this.showEmptyMsgError = false;
                    }, 3000);
                    return;
                }
            }
            this.$emit("sendMessage", this.files);
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "New Chat-Send Message"
            });
            this.files = [];
            this.expandFilePreview = false;
            this.replyMessage = null;
        },

        cancelReply() {
            this.replyMessage = null;
        },

        getMarkdownFromHtml(html: string) {
            return this._turndownService.turndown(html);
        },

        async getChatParticipantsList(filter: string) {
            const users = this.getMultipleUsers(this.selectedConversation?.loginIds);
            const data = users.filter((user: IConversationUser) => {
                const fullName = user.firstName + " " + user.lastName;
                return fullName.toLowerCase().includes(filter.trim());
            });
            if (data.length > 0) return data;
            return ["No Matches Found"];
        },

        replyMessageChanged(newValue: IMessageItem) {
            this.replyMessage = newValue;
        },

        initialValueChanged(newValue: string) {
            if (newValue !== this.result) {
                if (this.editorLoaded && !!this.editor) {
                    this.onLoadData = "";
                    if (!!newValue && newValue.length > 0) {
                        (this.editor as any).summernote("code", newValue);
                    } else {
                        (this.editor as any).summernote("code", "");
                    }
                } else {
                    if (!!newValue && newValue.length > 0) {
                        this.onLoadData = newValue;
                    }
                }
            }
        },

        async handleReplyMessageClicked(message: { message: IMessageItem }) {
            const { text } = formatTaggedUserMessage(
                message.message.text,
                message.message.placeHoldersList,
                this.loggedInUserDetails.loginId
            );
            message.message.text = text;
            const { sharedProfiles } = sortPlaceholdersList(message.message.placeHoldersList);
            this.replyMsgProfiles = sharedProfiles;
            this.replyMessage = message.message;
        },

        removeProfile(removedProfile: any) {
            const id = removedProfile.hasOwnProperty("id") ? removedProfile.id : removedProfile.patientId;
            const updatedProfileList = this.selectedProfile.filter(
                (profile: any) => (profile.hasOwnProperty("id") ? profile.id : profile.patientId) !== id
            );
            const updatedAllProfileList = this.allSelectedProfiles.filter(
                (profile: any) => (profile.hasOwnProperty("id") ? profile.id : profile.patientId) !== id
            );
            this.$emit("update:selectedProfile", updatedProfileList, updatedAllProfileList);
        },

        attachFileButton() {
            let ui = ($ as any).summernote.ui;

            // create button
            let button = ui.button({
                contents: `<i class="fas fa-paperclip" />`,
                tooltip: "Click to Attach a File",
                click: () => {
                    this.triggerFileUpload();
                }
            });

            return button.render(); // return button as jquery object
        },

        sendEditBtn() {
            let ui = ($ as any).summernote.ui;
            let button = ui.button({
                contents: `<i class="fas fa-check"></i>`,
                tooltip: `Send`,
                click: () => {
                    this.send();
                }
            });

            return button.render();
        },

        triggerFileUpload() {
            const toolbarRef = `${this.refsName}-uploadMarkup`;
            $(this.$refs?.[toolbarRef]).trigger("click");
        },

        getFileIcon(type: string) {
            if (type.includes("wordprocessing")) {
                return "fa-file-word";
            } else if (type.includes("spreadsheet")) {
                return "fa-file-excel";
            } else if (type.includes("pdf")) {
                return "fa-file-pdf";
            } else if (type.match("image.*")) {
                return "fa-image";
            } else {
                return "fa-file";
            }
        },

        async uploadFilesChanged(event: any, isFilePasted: boolean = false) {
            if (isFilePasted) {
                this.uploadFile = event;
            } else {
                this.uploadFile = event.target.files;
            }
            this.showAttachmentLimitError = this.uploadFile.length > 25;
            const existingFileNames = new Set(this.files.map((file) => file.name));
            const uniqueFiles = Array.from(this.uploadFile).filter((file) => {
                if (existingFileNames.has(file.name)) {
                    // Show warning message if file is a duplicate
                    this.showDuplicateAttachmentError = true;
                    setTimeout(() => {
                        this.showDuplicateAttachmentError = false;
                    }, 5000);
                    return false;
                }
                existingFileNames.add(file.name);
                return true;
            });
            // Add only unique files to the existing files list
            this.files = [...this.files, ...uniqueFiles];
            this.expandFilePreview = this.files.length > 0;
            this.$emit("update:msgFiles", this.files);
        },

        removeFile(fileName: string) {
            this.files = this.files.filter((file) => file.name !== fileName);
            this.expandFilePreview = this.files.length > 0;
            this.$emit("update:msgFiles", this.files);
        },

        reset() {
            if (this.editorLoaded) {
                this.expandFilePreview = false;
                this.files = [];
                this.destroy();
                // summernote leaves the html message behind after destruction, this removes it
                this.editorMarkupRef.innerHTML = "";
                this.create();
                ($(this.editorMarkupRef) as any).summernote("reset");
            }
        },

        destroy() {
            this.editor.off("summernote.change");
            this.editor.off("summernote.keydown");
            ($(this.editorMarkupRef) as any).summernote("destroy");
            this.editor = null;
            this.replyMessageRef = null;
            this.editorToolbarRef = null;
            this.replyMessage = null;
        }
    },
    unmounted() {
        this.destroy();
    }
});
